import React, { useCallback, useEffect, useRef } from "react";
import shutter from "../../../assets/shutter.svg";

/**
 * CameraCapture component - Handles camera shutter and timer functionality
 * 
 * This component manages the photo capture process including:
 * - Instant and timed photo capture with visual countdown
 * - Image processing with aspect ratio constraints and filters
 * - State management for capture workflow
 * - Error handling for camera failures
 * 
 * @param {Object} props - Component props
 * @param {React.RefObject} webcamRef - Reference to webcam component for screenshot capture
 * @param {React.RefObject} previewRef - Reference to preview container for dimension calculations
 * @param {Function} setCameraError - Function to set camera error state
 * @param {string|null} cameraError - Current camera error state
 * @param {React.RefObject} canvasRef - Reference to canvas element for image processing
 * @param {Object} filterMap - Map of available CSS filters with their values
 * @param {string} activeFilter - Currently active filter name
 * @param {Function} addImage - Function to add processed image to state
 * @param {number} timer - Timer duration in seconds (0=immediate, 3=3sec, 10=10sec)
 * @param {Array} selectedImages - Currently selected/captured images array
 * @param {Function} setCountdown - Function to set countdown display value
 * @param {number|null} countdown - Current countdown value or null
 * @param {string} ratio - Selected aspect ratio (1:1, 3:4, or 9:16)
 * @returns {JSX.Element} - Shutter button component with countdown overlay
 */
const CameraCapture = ({
  webcamRef, // Reference to webcam component for screenshot access
  previewRef, // Reference to preview container for dimension calculations
  setCameraError, // Function to update camera error state
  cameraError, // Current camera error message (null if no error)
  canvasRef, // Reference to canvas element for image processing and filtering
  filterMap, // Object mapping filter names to CSS filter strings
  activeFilter, // Currently selected filter name from filterMap
  addImage, // Function to add processed image to the images array
  timer, // Timer duration: 0 (immediate), 3, or 10 seconds
  selectedImages, // Array of currently captured/selected images
  setCountdown, // Function to update the countdown display value
  countdown, // Current countdown number (null when not counting down)
  ratio, // Target aspect ratio for captured images (e.g., "1:1", "3:4", "9:16")
}) => {
  // Reference for managing the countdown interval timer
  const timerRef = useRef(null);
  
  // Flag to prevent multiple simultaneous countdowns
  // Using ref instead of state to avoid re-renders during countdown
  const isCountingRef = useRef(false);

  /**
   * Applies filters to image data for iOS compatibility
   * 
   * iOS Safari has limited support for canvas CSS filters, so we implement
   * pixel-level manipulation for common filter effects.
   * 
   * @param {Uint8ClampedArray} data - Image pixel data array
   * @param {string} filterType - Type of filter to apply
   * @returns {void} - Modifies data array in place
   */
  const applyIOSFilter = (data, filterType) => {
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];     // Red
      const g = data[i + 1]; // Green
      const b = data[i + 2]; // Blue
      const a = data[i + 3]; // Alpha
      
      let newR = r, newG = g, newB = b;
      
      switch (filterType) {
        case 'BW':
          // Convert to grayscale
          const gray = r * 0.299 + g * 0.587 + b * 0.114;
          newR = newG = newB = gray * 1.1; // Enhanced contrast
          break;
          
        case 'Sepia':
          // Sepia tone conversion
          newR = Math.min(255, (r * 0.393) + (g * 0.769) + (b * 0.189));
          newG = Math.min(255, (r * 0.349) + (g * 0.686) + (b * 0.168));
          newB = Math.min(255, (r * 0.272) + (g * 0.534) + (b * 0.131));
          break;
          
        case 'Warm':
          // Warm tones
          newR = Math.min(255, r * 1.05 * 1.3 * 0.8 + g * 1.3 * 0.2);
          newG = Math.min(255, g * 1.05 * 1.3 * 0.8 + r * 1.3 * 0.2);
          newB = Math.min(255, b * 1.05 * 1.3 * 0.8);
          break;
          
        case 'Cool':
          // Cool tones
          newR = Math.min(255, r * 1.1 * 0.85 * 0.99);
          newG = Math.min(255, g * 1.1 * 0.85 * 0.99);
          newB = Math.min(255, b * 1.1 * 0.85 * 0.99);
          break;
          
        case 'Vintage':
          // Vintage effect
          newR = Math.min(255, (r * 0.393) + (g * 0.769) + (b * 0.189) * 1.1 * 1.05);
          newG = Math.min(255, (r * 0.349) + (g * 0.686) + (b * 0.168) * 1.1 * 1.05);
          newB = Math.min(255, (r * 0.272) + (g * 0.534) + (b * 0.131) * 1.1 * 1.05);
          break;
          
        case 'Vivid':
          // Highly saturated
          newR = Math.min(255, r * 1.6 * 1.15);
          newG = Math.min(255, g * 1.6 * 1.15);
          newB = Math.min(255, b * 1.6 * 1.15);
          break;
          
        case 'Invert':
          // Color inversion
          newR = 255 - r;
          newG = 255 - g;
          newB = 255 - b;
          break;
          
        default:
          // For other filters, apply basic adjustments
          if (filterType !== 'Normal') {
            // Simple brightness/contrast adjustment
            newR = Math.min(255, r * 1.05);
            newG = Math.min(255, g * 1.05);
            newB = Math.min(255, b * 1.05);
          }
          break;
      }
      
      data[i] = newR;
      data[i + 1] = newG;
      data[i + 2] = newB;
      // Alpha channel remains unchanged
    }
  };

  /**
   * Calculates crop dimensions to match target aspect ratio
   * 
   * This function determines the optimal crop area to extract from a source image
   * to match the desired aspect ratio while maintaining image quality and centering.
   * 
   * @param {number} imgW - Source image width in pixels
   * @param {number} imgH - Source image height in pixels
   * @param {string} ratio - Target aspect ratio (e.g., "1:1", "3:4", "9:16")
   * @returns {Object} Crop coordinates and dimensions for canvas drawing
   * @returns {number} returns.sx - Source X coordinate (left edge of crop area)
   * @returns {number} returns.sy - Source Y coordinate (top edge of crop area)
   * @returns {number} returns.sw - Source width of crop area
   * @returns {number} returns.sh - Source height of crop area
   */
  const getCropSize = (imgW, imgH, ratio) => {
    // Parse ratio string "16:9" to get numeric values [16, 9]
    const [rw, rh] = ratio.split(":").map(Number);
    const targetRatio = rw / rh;

    // Start with full image width and calculate corresponding height
    let newW = imgW;
    let newH = imgW / targetRatio;

    // If calculated height exceeds source image, adjust to fit height
    // This happens when source image is more portrait than target ratio
    if (newH > imgH) {
      newH = imgH;
      newW = imgH * targetRatio;
    }

    // Return centered crop coordinates
    // (imgW - newW) / 2 centers the crop horizontally
    // (imgH - newH) / 2 centers the crop vertically
    return {
      sx: (imgW - newW) / 2, // Source X (centered horizontal crop)
      sy: (imgH - newH) / 2, // Source Y (centered vertical crop)
      sw: newW, // Source width (cropped area width)
      sh: newH, // Source height (cropped area height)
    };
  };

  /**
   * Processes captured image with filters and aspect ratio constraints
   * 
   * This function handles the complete image processing pipeline:
   * 1. Calculates optimal canvas dimensions based on preview container
   * 2. Crops source image to match target aspect ratio
   * 3. Creates both original (unfiltered) and filtered versions
   * 4. Adds processed images to the application state
   * 
   * @param {HTMLImageElement} img - The captured image element to process
   * @returns {void} - Calls addImage function with processed results
   */
  const processImage = useCallback(
    (img) => {
      // Validate required DOM elements are available
      const preview = previewRef.current;
      const canvas = canvasRef.current;
      if (!preview || !canvas) return;

      // Parse target aspect ratio from string "16:9" to numeric ratio
      const [rw, rh] = ratio.split(":").map(Number);
      const targetRatio = rw / rh;

      // Get preview container dimensions for canvas sizing
      const maxW = preview.clientWidth;
      const maxH = preview.clientHeight;

      // Calculate canvas dimensions that fit within preview while maintaining aspect ratio
      let cw, ch; // canvas width and height
      if (maxW / maxH > targetRatio) {
        // Preview is wider than target ratio - height is limiting factor
        ch = maxH;
        cw = maxH * targetRatio;
      } else {
        // Preview is taller than target ratio - width is limiting factor
        cw = maxW;
        ch = maxW / targetRatio;
      }

      // Set canvas dimensions for proper image rendering
      canvas.width = cw;
      canvas.height = ch;

      // Calculate crop dimensions for source image
      const crop = getCropSize(img.width, img.height, ratio);

      /* ===== ORIGINAL IMAGE PROCESSING ===== */
      // Create separate canvas for original (unfiltered) image
      const oCanvas = document.createElement("canvas");
      oCanvas.width = cw;
      oCanvas.height = ch;
      const oCtx = oCanvas.getContext("2d");

      // Draw cropped original image to canvas
      oCtx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, cw, ch);

      // Convert canvas to data URL for storage
      const original = oCanvas.toDataURL("image/png");

      /* ===== FILTERED IMAGE PROCESSING ===== */
      // Use main canvas for filtered image
      const ctx = canvas.getContext("2d");
      
      // Check if iOS or Safari and apply filter workaround
      console.log(window.navigator)
      const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
      const needsWorkaround = isIOS || isSafari;
      
      if (needsWorkaround && activeFilter !== "Normal") {
        // iOS/Safari doesn't support canvas filters well - apply pixel manipulation
        ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, cw, ch);
        
        // Get image data for pixel manipulation
        const imageData = ctx.getImageData(0, 0, cw, ch);
        const data = imageData.data;
        
        console.log('Applying iOS/Safari filter:', activeFilter, 'isIOS:', isIOS, 'isSafari:', isSafari);
        
        // Apply filter based on activeFilter type
        applyIOSFilter(data, activeFilter);
        
        // Put modified image data back
        ctx.putImageData(imageData, 0, 0);
      } else {
        // Apply CSS filter to canvas context
        console.log('Applying CSS filter:', activeFilter);
        ctx.filter = filterMap[activeFilter];
        ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, cw, ch);
      }

      // Convert filtered canvas to data URL
      const filtered = canvas.toDataURL("image/png");

      /* ===== ADD TO APPLICATION STATE ===== */
      // Add both image versions to the application state
      // This payload structure matches GallerySelect for consistency
      addImage({
        original, // Unfiltered image data URL
        filtered, // Filtered image data URL
        isLandscape: cw > ch, // Orientation flag for UI layout
        source: "camera", // Source identifier for tracking
      });
    },
    [activeFilter, ratio], // Dependencies for useCallback optimization
  );

  /**
   * Captures photo immediately from webcam
   * 
   * This function handles the immediate capture workflow:
   * 1. Validates all required components are available
   * 2. Checks if images exist (reset mode) or camera is ready
   * 3. Captures screenshot from webcam
   * 4. Creates image object and triggers processing
   * 
   * @returns {void} - Triggers image processing or resets state
   */
  const captureNow = () => {
    // Validate required refs are available and no camera errors exist
    if (!webcamRef.current || !previewRef.current || cameraError) return;

    // If images already selected, clear them and return to camera view
    // This provides a reset functionality when user wants to take new photos
    if (selectedImages.length) {
      setCameraError(null); // Clear any existing errors
      return; // Exit without capturing
    }

    // Capture screenshot from webcam component
    const screenshot = webcamRef.current.getScreenshot();
    if (!screenshot) return; // Exit if screenshot failed

    // Create Image object to load and process the screenshot
    const img = new Image();
    img.src = screenshot;

    // Set up event handlers for image loading
    img.onload = () => processImage(img); // Process when image loads successfully
    img.onerror = () => setCameraError("CAPTURE_FAILED"); // Handle loading errors
  };

  /**
   * Handles timer functionality and initiates photo capture
   * 
   * This function manages the complete timer workflow:
   * 1. Validates capture conditions (no errors, no active countdown)
   * 2. Handles reset mode when images exist
   * 3. Manages instant capture (timer=0) or countdown timer
   * 4. Controls countdown display and state management
   * 5. Ensures proper cleanup of timer intervals
   * 
   * @returns {void} - Initiates capture or timer countdown
   */
  const startTimerAndCapture = () => {
    // Prevent capture during camera errors or active countdown
    if (cameraError || isCountingRef.current) return;

    // If images already selected, clear them and return to camera view
    // This provides the reset functionality when user wants to take new photos
    if (selectedImages?.length) {
      addImage({
        original: null, // Clear original image
        filtered: null, // Clear filtered image
      });
      return; // Exit without starting timer
    }

    // Instant capture if no timer is set (timer = 0 or falsy)
    if (!timer || timer === 0) {
      captureNow(); // Capture immediately
      return;
    }

    // Start countdown timer for delayed capture
    let timeLeft = timer; // Initialize countdown with timer duration
    isCountingRef.current = true; // Mark countdown as active to prevent duplicates
    setCountdown(timeLeft); // Display initial countdown value

    // Set up interval to update countdown every second
    timerRef.current = setInterval(() => {
      timeLeft -= 1; // Decrement countdown
      setCountdown(timeLeft); // Update display

      // When countdown reaches zero, capture the photo
      if (timeLeft === 0) {
        clearInterval(timerRef.current); // Clear the interval
        timerRef.current = null; // Reset timer reference
        setCountdown(null); // Hide countdown display
        isCountingRef.current = false; // Reset countdown flag
        captureNow(); // Capture the photo
      }
    }, 1000); // Execute every 1000ms (1 second)
  };

  /**
   * Cleanup effect for timer interval
   * 
   * This useEffect ensures proper cleanup of the countdown interval
   * when the component unmounts, preventing memory leaks and
   * continued timer execution after component removal.
   */
  useEffect(() => {
    // Return cleanup function that runs on component unmount
    return () => {
      // Clear any active countdown interval
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // Empty dependency array - runs only on mount/unmount

  return (
    <>
      {/* 
        Shutter button with disabled state during countdown
        - Shows shutter icon as visual indicator
        - Disabled when countdown is active to prevent multiple captures
        - Triggers timer and capture functionality on click
        - Styled with CSS classes for appearance and states
      */}
      <button
        className="shutter-btn"
        onClick={startTimerAndCapture} // Handle timer and capture logic
        disabled={countdown !== null} // Disable during countdown to prevent multiple triggers
      >
        {/* Shutter icon SVG - visual indicator for camera capture */}
        <img src={shutter} alt="Shutter" className="shutter-icon" />
      </button>
    </>
  );
};

export default CameraCapture;
